import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  askWaitingWorkerToSkip,
  register as registerServiceWorker,
} from '../serviceWorkerRegistration';
import {
  dismissInstallPrompt,
  getInstallPlatform,
  getManualInstallSteps,
  isStandaloneDisplay,
} from '../utils/pwa';

/**
 * Owns PWA install prompt + update state for the app chrome.
 */
export default function usePwa() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [updateRegistration, setUpdateRegistration] = useState(null);
  const [standalone, setStandalone] = useState(() => isStandaloneDisplay());
  const [online, setOnline] = useState(() =>
    (typeof navigator === 'undefined' ? true : navigator.onLine)
  );
  const [showManualHelp, setShowManualHelp] = useState(false);
  const platform = useMemo(() => getInstallPlatform(), []);
  const manual = useMemo(() => getManualInstallSteps(platform), [platform]);

  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  useEffect(() => {
    const syncStandalone = () => setStandalone(isStandaloneDisplay());
    syncStandalone();
    let mql;
    try {
      mql = window.matchMedia('(display-mode: standalone)');
      if (mql.addEventListener) mql.addEventListener('change', syncStandalone);
      else if (mql.addListener) mql.addListener(syncStandalone);
    } catch {
      // ignore
    }
    return () => {
      try {
        if (!mql) return;
        if (mql.removeEventListener) mql.removeEventListener('change', syncStandalone);
        else if (mql.removeListener) mql.removeListener(syncStandalone);
      } catch {
        // ignore
      }
    };
  }, []);

  useEffect(() => {
    const onBip = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setDeferredPrompt(null);
      setShowManualHelp(false);
      setStandalone(true);
    };
    window.addEventListener('beforeinstallprompt', onBip);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBip);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try {
        await deferredPrompt.userChoice;
      } catch {
        // ignore
      }
      setDeferredPrompt(null);
      return;
    }
    setShowManualHelp(true);
  }, [deferredPrompt]);

  useEffect(() => {
    registerServiceWorker({
      onUpdate: (registration) => setUpdateRegistration(registration),
      onSuccess: () => {
        // App shell cached for offline — no toast required.
      },
    });

    // When the waiting worker activates after SKIP_WAITING, reload once.
    let refreshing = false;
    const onControllerChange = () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    };
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
    }
    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
      }
    };
  }, []);

  // Landing / deep-link CTA: window.dispatchEvent(new Event('testera:pwa-install'))
  useEffect(() => {
    const onInstallEvent = () => {
      if (isStandaloneDisplay()) return;
      promptInstall();
    };
    window.addEventListener('testera:pwa-install', onInstallEvent);
    return () => window.removeEventListener('testera:pwa-install', onInstallEvent);
  }, [promptInstall]);

  const canNativeInstall = Boolean(deferredPrompt) && !standalone;
  const canShowInstallChrome = false;

  const dismissInstall = useCallback(() => {
    dismissInstallPrompt();
    setShowManualHelp(false);
  }, []);

  const applyUpdate = useCallback(() => {
    askWaitingWorkerToSkip(updateRegistration);
    // Fallback reload if controllerchange does not fire.
    setTimeout(() => {
      if (updateRegistration && updateRegistration.waiting) {
        window.location.reload();
      }
    }, 1500);
  }, [updateRegistration]);

  const dismissUpdate = useCallback(() => {
    setUpdateRegistration(null);
  }, []);

  return {
    online,
    standalone,
    platform,
    manual,
    canNativeInstall,
    canShowInstallChrome,
    showManualHelp,
    setShowManualHelp,
    promptInstall,
    dismissInstall,
    updateAvailable: Boolean(updateRegistration && updateRegistration.waiting),
    applyUpdate,
    dismissUpdate,
  };
}
